import React, { useState } from 'react';
import { Modal, Button, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Form, notify } from '@/components';
import { useAddProjectMutation, useUpdateProjectMutation } from '@/redux';
import { ProjectDto, ProjectsDto } from '@/core';
import { FormValueType, useSchemaProject } from './hooks/useSchemaProject';
import classes from './ProjectModal.module.scss';

interface ProjectModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  stopEdit: () => void;
  editedProject: ProjectDto | null;
  projectsListData: ProjectsDto;
}

const getInitialValue = (editedProject: ProjectDto | null): FormValueType => ({
  name: editedProject?.name ?? '',
  description: editedProject?.description ?? '',
});

enum SubmitButtonsEnum {
  Add = 'Add',
  AddAndContinue = 'Continue',
}

export const ProjectModal = ({
  isModalOpen,
  setIsModalOpen,
  stopEdit,
  editedProject,
  projectsListData,
}: ProjectModalProps) => {
  const navigate = useNavigate();
  const [createProject] = useAddProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [clickedButton, setClickedButton] = useState<SubmitButtonsEnum>();
  const { t } = useTranslation();
  const initialValue = getInitialValue(editedProject);
  const schema = useSchemaProject(projectsListData, initialValue);

  const handleSubmit = (projectData: FormValueType) => {
    if (editedProject) {
      updateProject({ value: { ...projectData }, id: editedProject.id })
        .unwrap()
        .then(() => notify.success.t('projects.editProject.success', { projectName: projectData.name }));
    } else {
      createProject({ ...projectData })
        .unwrap()
        .then((result) => {
          notify.success.t('projects.createProject.success', { projectName: projectData.name });
          clickedButton === SubmitButtonsEnum.AddAndContinue &&
            navigate(`/projects/constructor/${result.id}`);
        });
    }
    stopEdit();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    stopEdit();
    setIsModalOpen(false);
  };

  return (
    <Modal
      title={editedProject ? t('projects.editProject.title') : t('projects.createProject.title')}
      visible={isModalOpen}
      destroyOnClose={true}
      onCancel={handleCancel}
      width={485}
      className={classes.modalWrapper}
      footer={
        <Row
          className={editedProject ? classes.modalControls__edit : classes.modalControls}
          justify="space-between"
        >
          <Button key="cancel" type="default" size="large" onClick={handleCancel}>
            {t('projects.createProject.cancel')}
          </Button>
          <div className={editedProject ? classes.submitButtons__edit : classes.submitButtons}>
            <Form.Submit
              onClick={() => setClickedButton(SubmitButtonsEnum.Add)}
              size="large"
              key="create"
              type={editedProject ? 'primary' : 'default'}
              uid="add-project"
            >
              {editedProject ? t('projects.editProject.edit') : t('projects.createProject.add')}
            </Form.Submit>
            {!editedProject && (
              <Form.Submit
                size="large"
                onClick={() => setClickedButton(SubmitButtonsEnum.AddAndContinue)}
                uid="add-project"
                type="primary"
                className={classes.btnContinue}
              >
                {t('projects.createProject.continue')}
              </Form.Submit>
            )}
          </div>
        </Row>
      }
    >
      <Form.Formik<FormValueType>
        uid="add-project"
        i18nKeyPrefix="projects.createProject"
        initialValues={initialValue}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        <div className={classes.nameInput}>
          <Form.Input required name="name" />
        </div>
        <div className={classes.descriptionInput}>
          <Form.TextArea name="description" />
        </div>
      </Form.Formik>
    </Modal>
  );
};
